import React from 'react';
import {
  Box,
  Button,
  ButtonIcon,
  ButtonText,
  HStack,
  Input,
  InputField,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { ThumbsDown, ThumbsUp } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { useSubmitAiTutorEvaluationFeedback } from '@/common/api/endpoints/ai-tutor/ai-tutor';
import type { StudentEvaluationFeedback } from '@/common/api/models';
import { useOrbReducedMotion } from '@/common/components/presence/useOrbReducedMotion';

export type AiTutorFormativeResponseVariant = 'presence' | 'card';

type Props = {
  courseId: string;
  moduleId: string;
  taskId: string;
  observation: string;
  nextStep: string;
  formativeFeedbackVersion: number;
  runId?: string | null;
  isReadyToProgress?: boolean;
  initialFeedback?: StudentEvaluationFeedback;
  readonly?: boolean;
  hideStudentFeedbackControls?: boolean;
  variant?: AiTutorFormativeResponseVariant;
  animateEntry?: boolean;
};

const RESPONSE_ENTRY_MS = 250;

const AiTutorFormativeResponse: React.FC<Props> = ({
  courseId,
  moduleId,
  taskId,
  observation,
  nextStep,
  formativeFeedbackVersion,
  runId = null,
  isReadyToProgress = false,
  initialFeedback,
  readonly = false,
  hideStudentFeedbackControls = false,
  variant = 'presence',
  animateEntry = false,
}) => {
  const { t } = useTranslation();
  const reducedMotion = useOrbReducedMotion();
  const feedbackMutation = useSubmitAiTutorEvaluationFeedback();
  const lastSeenVersionRef = React.useRef<number>(0);
  const [selectedHelpful, setSelectedHelpful] = React.useState<boolean | null>(
    initialFeedback?.isHelpful ?? null,
  );
  const [showReasonInput, setShowReasonInput] = React.useState(false);
  const [negativeReason, setNegativeReason] = React.useState('');
  const [feedbackError, setFeedbackError] = React.useState<string | null>(null);
  const [entryVisible, setEntryVisible] = React.useState(!animateEntry || reducedMotion);

  const observationLabel = variant === 'presence'
    ? t('student.task.aiTutor.presenceBand.whatISee')
    : t('student.task.aiTutor.feedback.observationLabel');
  const nextStepLabel = variant === 'presence'
    ? (isReadyToProgress
      ? t('student.task.aiTutor.presenceBand.optionalPolish')
      : t('student.task.aiTutor.presenceBand.suggestedNextStep'))
    : t('student.task.aiTutor.feedback.nextStepLabel');

  const testIdPrefix = variant === 'presence' ? 'ai-tutor-presence-response' : 'ai-tutor-feedback-response';

  React.useEffect(() => {
    setSelectedHelpful(initialFeedback?.isHelpful ?? null);
    setShowReasonInput(false);
    setFeedbackError(null);
    setNegativeReason(initialFeedback?.isHelpful === false ? (initialFeedback?.reason ?? '') : '');
  }, [initialFeedback?.isHelpful, initialFeedback?.reason]);

  React.useEffect(() => {
    if (lastSeenVersionRef.current === 0) {
      lastSeenVersionRef.current = formativeFeedbackVersion;
      return;
    }
    if (formativeFeedbackVersion > lastSeenVersionRef.current) {
      lastSeenVersionRef.current = formativeFeedbackVersion;
      setSelectedHelpful(null);
      setShowReasonInput(false);
      setNegativeReason('');
      setFeedbackError(null);
    }
  }, [formativeFeedbackVersion]);

  React.useEffect(() => {
    if (!animateEntry || reducedMotion) {
      setEntryVisible(true);
      return undefined;
    }
    setEntryVisible(false);
    const frame = requestAnimationFrame(() => setEntryVisible(true));
    return () => cancelAnimationFrame(frame);
  }, [animateEntry, reducedMotion, observation, nextStep, formativeFeedbackVersion]);

  const submitFeedback = React.useCallback(async (isHelpful: boolean, reason: string | null = null) => {
    if (readonly || feedbackMutation.isPending || selectedHelpful !== null) return;
    setFeedbackError(null);
    try {
      await feedbackMutation.mutateAsync({
        courseId,
        moduleId,
        taskId,
        data: {
          isHelpful,
          category: null,
          runId: runId ?? null,
          reason,
        },
      });
      setSelectedHelpful(isHelpful);
      setShowReasonInput(false);
      setFeedbackError(null);
    } catch {
      setFeedbackError(t('student.task.aiTutor.feedback.submitError'));
    }
  }, [readonly, feedbackMutation, selectedHelpful, courseId, moduleId, taskId, runId, t]);

  const submitNegativeFeedback = React.useCallback(async () => {
    const reason = negativeReason.trim();
    if (!reason) return;
    await submitFeedback(false, reason);
  }, [negativeReason, submitFeedback]);

  const labelTextProps = variant === 'presence'
    ? {
      size: 'xs' as const,
      color: '$textSubtle',
      fontWeight: 'semibold' as const,
      textTransform: 'uppercase' as const,
      letterSpacing: '$sm' as const,
    }
    : {
      size: 'xs' as const,
      color: '$textHint',
      fontWeight: 'bold' as const,
      textTransform: 'uppercase' as const,
    };

  const entryTransitionStyle = animateEntry && !reducedMotion
    ? {
      transitionProperty: 'opacity',
      transitionDuration: `${RESPONSE_ENTRY_MS}ms`,
      transitionTimingFunction: 'ease-out',
    }
    : undefined;

  const nextStepLabelProps = variant === 'presence' && isReadyToProgress
    ? {
      size: 'xs' as const,
      color: '$textSubtle',
      fontWeight: 'semibold' as const,
      textTransform: 'uppercase' as const,
      letterSpacing: '$sm' as const,
    }
    : labelTextProps;

  const content = (
    <VStack space="md">
      {variant === 'presence' && isReadyToProgress ? (
        <Text
          size="sm"
          color="$success700"
          testID={`${testIdPrefix}-ready`}
        >
          {t('student.task.aiTutor.presenceBand.ready')}
        </Text>
      ) : null}
      <VStack space="xs">
        <Text
          {...labelTextProps}
          testID={`${testIdPrefix}-observation-label`}
        >
          {observationLabel}
        </Text>
        <Text size="sm" testID={`${testIdPrefix}-observation`}>
          {observation}
        </Text>
      </VStack>

      <VStack space="xs">
        <Text
          {...nextStepLabelProps}
          testID={`${testIdPrefix}-next-step-label`}
        >
          {nextStepLabel}
        </Text>
        <Text size="sm" testID={`${testIdPrefix}-next-step`}>
          {nextStep}
        </Text>
      </VStack>

      {!readonly && !hideStudentFeedbackControls ? (
        <VStack space="sm" pt="$2">
          <Text size="xs" color="$textSubtle">
            {t('student.task.aiTutor.feedback.helpfulPrompt')}
          </Text>
          <HStack space="sm">
            <Button
              size="sm"
              variant={selectedHelpful === true ? 'solid' : 'outline'}
              action={selectedHelpful === true ? 'positive' : 'secondary'}
              onPress={() => submitFeedback(true)}
              isDisabled={feedbackMutation.isPending || selectedHelpful !== null}
              testID={`${testIdPrefix}-helpful-yes`}
            >
              <ButtonIcon as={ThumbsUp} size="sm" mr="$2" />
              <ButtonText>{t('student.task.aiTutor.feedback.helpfulYes')}</ButtonText>
            </Button>
            <Button
              size="sm"
              variant={selectedHelpful === false ? 'solid' : 'outline'}
              action={selectedHelpful === false ? 'negative' : 'secondary'}
              onPress={() => {
                setShowReasonInput(true);
                setFeedbackError(null);
              }}
              isDisabled={feedbackMutation.isPending || selectedHelpful !== null}
              testID={`${testIdPrefix}-helpful-no`}
            >
              <ButtonIcon as={ThumbsDown} size="sm" mr="$2" />
              <ButtonText>{t('student.task.aiTutor.feedback.helpfulNo')}</ButtonText>
            </Button>
          </HStack>
          {showReasonInput && selectedHelpful === null ? (
            <VStack space="sm">
              <Input size="sm">
                <InputField
                  value={negativeReason}
                  onChangeText={setNegativeReason}
                  placeholder={t('student.task.aiTutor.feedback.reasonPlaceholder')}
                  maxLength={140}
                  testID={`${testIdPrefix}-reason-input`}
                />
              </Input>
              <HStack justifyContent="flex-end">
                <Button
                  size="sm"
                  action="negative"
                  onPress={submitNegativeFeedback}
                  isDisabled={feedbackMutation.isPending || negativeReason.trim().length === 0}
                  testID={`${testIdPrefix}-reason-submit`}
                >
                  <ButtonText>{t('student.task.aiTutor.feedback.submitReason')}</ButtonText>
                </Button>
              </HStack>
              {!!feedbackError && (
                <Text size="xs" color="$statusError" testID={`${testIdPrefix}-feedback-error`}>
                  {feedbackError}
                </Text>
              )}
            </VStack>
          ) : null}
        </VStack>
      ) : null}
    </VStack>
  );

  if (variant === 'card') {
    return (
      <Box testID={testIdPrefix}>
        {content}
      </Box>
    );
  }

  return (
    <Box
      testID={testIdPrefix}
      opacity={entryVisible ? 1 : 0}
      sx={entryTransitionStyle}
    >
      <Box px="$6" pb="$4">
        {content}
      </Box>
    </Box>
  );
};

export default AiTutorFormativeResponse;
